import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";
import { SkuCombobox } from "@/components/SkuCombobox";

const canReceive = (role) => role === "OWNER" || role === "ADMIN_INVENTORY";
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`);

export default function ReceivingForm() {
  const { purchaseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [progress, setProgress] = useState(null);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState({});       // purchase_item_id -> {product_id, physical, good, damaged, wrong, wrong_product_id, wrong_qty}
  const [idemKey, setIdemKey] = useState(newKey());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/receipts/purchase/${purchaseId}/progress`).then(({ data }) => {
      setProgress(data);
      const init = {};
      data.items.filter((it) => it.outstanding_qty > 0).forEach((it) => {
        init[it.purchase_item_id] = { product_id: it.product_id, physical: String(it.outstanding_qty), good: String(it.outstanding_qty), damaged: "0", wrong: false, wrong_product_id: "", wrong_qty: "" };
      });
      setRows(init);
    }).catch((err) => { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); });
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.status === "ACTIVE"))).catch(() => {});
  }, [purchaseId]);

  const setField = (pid, field, value) => setRows((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: value } }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const items = Object.entries(rows).map(([piId, r]) => (
        r.wrong
          ? { purchase_item_id: piId, product_id: r.product_id, physical_qty: 0, qc_good_qty: 0, qc_damaged_qty: 0, wrong_product_id: r.wrong_product_id, wrong_qty: parseInt(r.wrong_qty || "0", 10) }
          : { purchase_item_id: piId, product_id: r.product_id, physical_qty: parseInt(r.physical || "0", 10), qc_good_qty: parseInt(r.good || "0", 10), qc_damaged_qty: parseInt(r.damaged || "0", 10) }
      ));
      const res = await api.post("/receipts", { purchase_id: purchaseId, idempotency_key: idemKey, items });
      navigate(`/receiving/${res.data.id}`);
    } catch (err) {
      console.error(err);
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setIdemKey(newKey()); // fresh key so the operator can correct & retry
      setLoading(false);
    }
  };

  if (user && !canReceive(user.role)) {
    return (<div className="app-shell"><AppTopBar active="receiving" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  const openItems = progress ? progress.items.filter((it) => it.outstanding_qty > 0) : [];

  return (
    <div className="app-shell">
      <AppTopBar active="receiving" />
      <main className="content">
        <Link to="/receiving" className="back-link" data-testid="back-to-list">← Kembali ke daftar</Link>
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        {progress && (
          <section className="panel" data-testid="receiving-form-panel">
            <div className="panel-head">
              <h2 className="panel-title">Terima Barang — {progress.purchase_no}</h2>
              <span className="status-pill on">{progress.status}</span>
            </div>
            <p className="hint">Masukkan qty fisik yang datang + hasil QC. Hanya GOOD masuk stock. Selisih/SKU salah masuk HOLD & butuh keputusan.</p>

            <form onSubmit={submit}>
              <div className="table-scroll" style={{ marginTop: 12 }}>
                <table className="data-table" data-testid="receiving-items-table">
                  <thead>
                    <tr><th>SKU</th><th>Outstanding</th><th>SKU Salah?</th><th>Physical</th><th>QC GOOD</th><th>QC DAMAGED</th></tr>
                  </thead>
                  <tbody>
                    {openItems.length === 0 && <tr><td colSpan={6} className="empty-cell">Tidak ada outstanding.</td></tr>}
                    {openItems.map((it) => {
                      const r = rows[it.purchase_item_id] || {};
                      return (
                        <tr key={it.purchase_item_id} data-testid={`receiving-row-${it.sku_code}`}>
                          <td className="mono">{it.sku_code}</td>
                          <td className="mono" data-testid={`outstanding-${it.sku_code}`}>{it.outstanding_qty}</td>
                          <td>
                            <input type="checkbox" checked={!!r.wrong} onChange={(e) => setField(it.purchase_item_id, "wrong", e.target.checked)} data-testid={`wrong-toggle-${it.sku_code}`} />
                          </td>
                          {r.wrong ? (
                            <td colSpan={3}>
                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <div style={{ flex: 1 }}>
                                  <SkuCombobox products={products} value={r.wrong_product_id} index={it.sku_code}
                                    onChange={(pid) => setField(it.purchase_item_id, "wrong_product_id", pid)} />
                                </div>
                                <input className="field-input narrow" type="number" min="1" placeholder="qty salah"
                                  value={r.wrong_qty} onChange={(e) => setField(it.purchase_item_id, "wrong_qty", e.target.value)}
                                  data-testid={`wrong-qty-${it.sku_code}`} />
                              </div>
                            </td>
                          ) : (
                            <>
                              <td><input className="field-input narrow" type="number" min="0" value={r.physical ?? ""} onChange={(e) => setField(it.purchase_item_id, "physical", e.target.value)} data-testid={`physical-${it.sku_code}`} /></td>
                              <td><input className="field-input narrow" type="number" min="0" value={r.good ?? ""} onChange={(e) => setField(it.purchase_item_id, "good", e.target.value)} data-testid={`good-${it.sku_code}`} /></td>
                              <td><input className="field-input narrow" type="number" min="0" value={r.damaged ?? ""} onChange={(e) => setField(it.purchase_item_id, "damaged", e.target.value)} data-testid={`damaged-${it.sku_code}`} /></td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="form-actions">
                <button className="btn-primary" type="submit" disabled={loading || openItems.length === 0} data-testid="post-receiving-button">
                  {loading ? "Memproses..." : "POST Receiving"}
                </button>
              </div>
            </form>
          </section>
        )}
      </main>
    </div>
  );
}
